import React from 'react';
import styles from './dialog.module.scss';
import clsx from 'clsx';

import Typography from '@smart-tv/components/typography';

import {
  useFocusable,
  FocusHandler,
  KeyPressDetails
} from '@noriginmedia/norigin-spatial-navigation';

interface DialogItemProps {
  selected?: boolean;
  isFocused?: boolean;
  focusKey?: string;
  onEnterPress: (props?: object, details?: KeyPressDetails) => void;
  onFocus?: FocusHandler;
  hasBackground?: boolean;
  focusedContainer?: boolean;
  hasIcon?: boolean;
  noMargin?: boolean;
  children: React.ReactNode | React.ReactNode[];
  itemProps?: any;
  onClick?: () => void;
  dataTestId?: string;
  className?: string;
  onPointerEnter?: () => void;
  onPointerLeave?: () => void;
}

export const DialogItem: React.FC<DialogItemProps> = (props: DialogItemProps) => {
  const {
    selected = false,
    isFocused = false,
    onEnterPress,
    onFocus,
    focusKey: focusKeyParam,
    hasBackground = false,
    focusedContainer = false,
    hasIcon = false,
    noMargin = false,
    itemProps = {},
    children,
    onClick,
    dataTestId,
    className,
    onPointerEnter,
    onPointerLeave
  } = props;

  const { ref, focused, focusSelf } = useFocusable({
    onFocus,
    onEnterPress: onEnterPress,
    focusKey: focusKeyParam
  });

  React.useEffect(() => {
    if (isFocused) {
      focusSelf();
    }
  }, [isFocused, focusSelf]);

  let dialogItemStateClass: string | undefined;
  let textStateClass: string | undefined;

  if (focused) {
    dialogItemStateClass = styles['dialog-item--focused'];
    textStateClass = styles['text--focused'];
  } else if (selected) {
    dialogItemStateClass = styles['dialog-item--selected'];
    textStateClass = styles['text--selected'];
  } else if (focusedContainer) {
    dialogItemStateClass = styles['dialog-item--focused-container'];
  } else if (hasBackground) {
    dialogItemStateClass = styles['dialog-item--background'];
  }

  const dialogItemClasses = clsx(
    styles['dialog-item'],
    dialogItemStateClass,
    noMargin && styles['dialog-item--no-margin'],
    className
  );

  const dialogTextClasses = clsx(styles['text'], textStateClass);

  const clickHandler = onClick ? onClick : onEnterPress;

  return (
    <div
      className={dialogItemClasses}
      ref={ref}
      onClick={clickHandler}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      data-testid={dataTestId}
      {...itemProps}
    >
      {hasIcon ? (
        <div className={styles['icon']}>{children}</div>
      ) : (
        <Typography className={dialogTextClasses} noWrap>
          {children}
        </Typography>
      )}
    </div>
  );
};

export default React.memo(DialogItem);
